import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/Button.tsx'
import { IconPlus, IconCopy, IconTrash, IconStar, IconStarFilled, IconAlertTriangle } from '@tabler/icons-react'
import { toast } from 'react-toastify'
import { useClient } from '@/stores/useClient.ts'
import { usePortalContext } from '@/pages/portal/Portal.page.tsx'
import { CustomPriceListSummary } from '@/models/priceList.ts'
import { ProfCommission, ProfPayout } from '@/models/profCommission.ts'
import { PaginatedResponse } from '@/stores/useClient.ts'
import { CreatePriceListModal, useCreatePriceListModal } from '@/components/portal/CreatePriceListModal.tsx'
import { DeleteConfirmModal, useDeleteConfirmModal } from '@/components/portal/DeleteConfirmModal.tsx'
import LoadBig from '@/components/LoadBig/LoadBig.tsx'
import PortalStandardComponent from '@/components/portal/PortalStandardComponent.tsx'
import PortalTabs, { PortalTab } from '@/components/portal/PortalTabs.tsx'

const shopTabs: PortalTab[] = [
  { id: 'pricelists', label: 'Preislisten' },
  { id: 'commissions', label: 'Provisionen' },
  { id: 'payouts', label: 'Auszahlungen' },
]

export default function PortalShopPage() {
  const [activeTab, setActiveTab] = useState('pricelists')

  return (
    <PortalStandardComponent title="Shop" description="Verdiene Geld, wenn Kunden Fotos über deine Galerien bestellen. Konfiguriere Preislisten und behalte deine Provisionen im Blick." noCard>
      <PortalTabs tabs={shopTabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'pricelists' && <PriceListsTab />}
      {activeTab === 'commissions' && <CommissionsTab />}
      {activeTab === 'payouts' && <PayoutsTab />}
    </PortalStandardComponent>
  )
}

// ── Preislisten Tab ──

function PriceListsTab() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const portalContext = usePortalContext()
  const { getPriceLists, createPriceList, deletePriceList, updatePortalMine } = useClient()
  const { modalRef: createModalRef, show: showCreateModal, close: closeCreateModal } = useCreatePriceListModal()
  const { modalRef: deleteModalRef, show: showDeleteModal, close: closeDeleteModal } = useDeleteConfirmModal()
  const [isCreating, setIsCreating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [listToDelete, setListToDelete] = useState<CustomPriceListSummary | null>(null)

  const { data: priceLists, isLoading } = useQuery({
    queryKey: ['priceLists'],
    queryFn: async () => {
      const res = await getPriceLists()
      return res.data as CustomPriceListSummary[]
    },
  })

  const defaultPriceListId = portalContext?.account?.default_price_list ?? null

  async function handleCreate(name: string) {
    setIsCreating(true)
    try {
      await createPriceList({ name })
      closeCreateModal()
      queryClient.invalidateQueries({ queryKey: ['priceLists'] })
      toast('Preisliste erstellt', { autoClose: 2000 })
    } catch (e: any) {
      console.error('Error creating price list:', e)
      toast.error('Fehler beim Erstellen der Preisliste')
    } finally {
      setIsCreating(false)
    }
  }

  async function handleClone(list: CustomPriceListSummary) {
    try {
      await createPriceList({ name: `${list.name} (Kopie)`, clone_from: list.id })
      queryClient.invalidateQueries({ queryKey: ['priceLists'] })
      toast('Preisliste dupliziert', { autoClose: 2000 })
    } catch (e: any) {
      console.error('Error cloning price list:', e)
      toast.error('Fehler beim Duplizieren der Preisliste')
    }
  }

  function handleDeleteClick(list: CustomPriceListSummary) {
    setListToDelete(list)
    setTimeout(() => showDeleteModal(), 0)
  }

  async function handleDeleteConfirm() {
    if (!listToDelete) return
    setIsDeleting(true)
    try {
      await deletePriceList(listToDelete.id)
      closeDeleteModal()
      setListToDelete(null)
      queryClient.invalidateQueries({ queryKey: ['priceLists'] })
      toast('Preisliste gelöscht', { autoClose: 2000 })
    } catch (e: any) {
      console.error('Error deleting price list:', e)
      toast.error('Fehler beim Löschen der Preisliste')
    } finally {
      setIsDeleting(false)
    }
  }

  async function handleSetDefault(listId: string | null) {
    try {
      await updatePortalMine({ default_price_list: listId })
      queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
      toast(listId ? 'Standard-Preisliste gesetzt' : 'Standard-Preisliste entfernt', { autoClose: 2000 })
    } catch (e: any) {
      console.error('Error setting default price list:', e)
      toast.error('Fehler beim Setzen der Standard-Preisliste')
    }
  }

  if (isLoading) return <LoadBig />

  return (
    <>
      <div className="bg-neutral p-5">
        <div className="flex items-center justify-end mb-4">
          <Button color="primary" onClick={showCreateModal}>
            <IconPlus size={20} />
            Neue Preisliste
          </Button>
        </div>

        {!priceLists || priceLists.length === 0 ? (
          <div className="p-8 text-center">
            <p className="opacity-60 mb-4">Noch keine Preislisten vorhanden.</p>
            <Button color="primary" variant="outline" onClick={showCreateModal}>
              <IconPlus size={20} />
              Erste Preisliste erstellen
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {priceLists.map(list => {
              const isDefault = list.id === defaultPriceListId
              return (
                <div
                  key={list.id}
                  className="card bg-secondary shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/portal/shop/${list.id}`)}
                >
                  <div className="card-body p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg truncate">{list.name}</h3>
                        <p className="text-sm opacity-60 mt-1">
                          {list.items_count} {list.items_count === 1 ? 'Produkt' : 'Produkte'}
                        </p>
                        <p className="text-xs opacity-40 mt-1">
                          Erstellt: {new Date(list.created_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                      {isDefault && (
                        <span className="badge badge-primary badge-sm ml-2 flex-shrink-0">Standard</span>
                      )}
                    </div>
                    <div className="card-actions justify-end mt-2" onClick={e => e.stopPropagation()}>
                      <button
                        className="btn btn-ghost btn-sm px-2"
                        title={isDefault ? 'Standard entfernen' : 'Als Standard setzen'}
                        onClick={() => handleSetDefault(isDefault ? null : list.id)}
                      >
                        {isDefault ? <IconStarFilled size={18} className="text-warning" /> : <IconStar size={18} />}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm px-2"
                        title="Duplizieren"
                        onClick={() => handleClone(list)}
                      >
                        <IconCopy size={18} />
                      </button>
                      {priceLists.length > 1 && (
                        <button
                          className="btn btn-ghost btn-sm px-2 text-error"
                          title="Löschen"
                          onClick={() => handleDeleteClick(list)}
                        >
                          <IconTrash size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <CreatePriceListModal
        modalRef={createModalRef}
        onConfirm={handleCreate}
        isLoading={isCreating}
      />

      <DeleteConfirmModal
        modalRef={deleteModalRef}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        title="Preisliste löschen"
        message={`Möchtest du die Preisliste „${listToDelete?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
      />
    </>
  )
}

// ── Shared ──

function PayoutMissingBanner() {
  const portalContext = usePortalContext()
  const account = portalContext?.account
  if (account?.iban && account?.iban_recipient_name) return null

  return (
    <div className="alert alert-warning mb-4">
      <IconAlertTriangle size={20} />
      <div>
        <p className="font-semibold">Auszahlungsdaten unvollständig</p>
        <p className="text-sm">Damit Provisionen ausgezahlt werden können, müssen IBAN und Empfängername in den Einstellungen hinterlegt sein.</p>
      </div>
      <Link to="/portal/settings?tab=payout">
        <Button size="sm" color="warning">Auszahlungsdaten hinterlegen</Button>
      </Link>
    </div>
  )
}

// ── Provisionen Tab ──

function formatEuro(value: string): string {
  return parseFloat(value).toFixed(2).replace('.', ',') + ' €'
}

function CommissionsTab() {
  const { getCommissions } = useClient()
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['commissions', page],
    queryFn: async () => {
      const res = await getCommissions({ page })
      return res.data as PaginatedResponse<ProfCommission>
    },
  })

  if (isLoading) return <LoadBig />

  const commissions = data?.results ?? []
  const totalPages = data ? Math.ceil(data.count / 20) : 1

  if (commissions.length === 0 && page === 1) {
    return (
      <>
        <PayoutMissingBanner />
        <div className="bg-neutral p-8 text-center">
          <p className="opacity-60">Noch keine Provisionen vorhanden.</p>
        </div>
      </>
    )
  }

  const hasOpenCommissions = commissions.some(c => !c.payout_status)

  return (
    <>
      <PayoutMissingBanner />
      <div className="bg-neutral p-5">
        {hasOpenCommissions && (
          <div className="alert alert-info mb-4">
            <span className="text-sm">Offene Provisionen werden einmal monatlich gesammelt ausgezahlt.</span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Bestellnr.</th>
                <th className="text-right">Kundensumme</th>
                <th className="text-right">Provision (Brutto)</th>
                <th className="text-right">Provision (Netto)</th>
                <th>Auszahlung</th>
                <th>Datum</th>
              </tr>
            </thead>
            <tbody>
              {commissions.map(c => (
                <tr key={c.id} className="hover">
                  <td className="font-mono text-sm">{c.order_number}</td>
                  <td className="text-right">{formatEuro(c.customer_total)}</td>
                  <td className="text-right">{formatEuro(c.commission_gross)}</td>
                  <td className="text-right">{formatEuro(c.commission_net)}</td>
                  <td>
                    {c.payout_status === 'completed' && <span className="badge badge-success badge-sm">Ausgezahlt</span>}
                    {c.payout_status === 'pending' && <span className="badge badge-warning badge-sm">Ausstehend</span>}
                    {!c.payout_status && <span className="badge badge-ghost badge-sm">Offen</span>}
                  </td>
                  <td className="text-sm">{new Date(c.created_at).toLocaleDateString('de-DE')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <Button size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Zurück
            </Button>
            <span className="flex items-center px-3 text-sm">
              Seite {page} von {totalPages}
            </span>
            <Button size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              Weiter
            </Button>
          </div>
        )}
      </div>
    </>
  )
}

// ── Auszahlungen Tab ──

function PayoutsTab() {
  const { getPayouts } = useClient()
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['payouts', page],
    queryFn: async () => {
      const res = await getPayouts({ page })
      return res.data as PaginatedResponse<ProfPayout>
    },
  })

  if (isLoading) return <LoadBig />

  const payouts = data?.results ?? []
  const totalPages = data ? Math.ceil(data.count / 20) : 1

  if (payouts.length === 0 && page === 1) {
    return (
      <>
        <PayoutMissingBanner />
        <div className="bg-neutral p-8 text-center">
          <p className="opacity-60">Noch keine Auszahlungen vorhanden.</p>
        </div>
      </>
    )
  }

  return (
    <>
      <PayoutMissingBanner />
      <div className="bg-neutral p-5">
      <div className="overflow-x-auto">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Empfänger</th>
              <th>IBAN</th>
              <th className="text-right">Betrag</th>
              <th className="text-right">Provisionen</th>
              <th>Status</th>
              <th>Datum</th>
            </tr>
          </thead>
          <tbody>
            {payouts.map(p => (
              <tr key={p.id} className="hover">
                <td>{p.recipient_name}</td>
                <td className="font-mono text-sm">{p.iban}</td>
                <td className="text-right">{formatEuro(p.payout_amount)}</td>
                <td className="text-right">{p.commissions_count}</td>
                <td>
                  {p.status === 'completed' && <span className="badge badge-success badge-sm">Abgeschlossen</span>}
                  {p.status === 'pending' && <span className="badge badge-warning badge-sm">Ausstehend</span>}
                </td>
                <td className="text-sm">{new Date(p.created_at).toLocaleDateString('de-DE')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <Button size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Zurück
            </Button>
            <span className="flex items-center px-3 text-sm">
              Seite {page} von {totalPages}
            </span>
            <Button size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              Weiter
            </Button>
          </div>
        )}
      </div>
    </>
  )
}
